import { Box, Button, Flex, Heading, Text, VStack } from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import SummaryTickets from "../../components/SummaryTickets/SummaryTickets";
import { useNavigate } from "react-router-dom";
import OrdersService from "../../../../services/orders/ordersService";
import {
    Cookie
} from '../../../events/pages/AllEventsPage/cookie-utils';
import { useAppToast } from "../../../../hooks/useAppToast";

const ConfirmationStep = ({ data, t }) => {
    const navigate = useNavigate();
    const [error, setError] = useState(null);
    const [order, setOrder] = useState(null);
    const [orderId, setOrderId] = useState(null);
    const redirectedRef = useRef(false);
    const toast = useAppToast();

    const eventId = data?.id;
    const isFree = !!data?.isFree;

    const buyer = useMemo(() => ({
        name: data?.firstName || "",
        surName: data?.lastName || "",
        email: data?.email || "",
        phone: data?.phone || "",
        info: data?.info || "",
    }), [data]);

    const items = useMemo(() => {
        if (isFree) return [];
        const q = data?.cart?.quantities || {};
        return Object.entries(q)
            .map(([eventTicketTypeId, quantity]) => ({
                eventTicketTypeId,
                quantity: Number(quantity) || 0,
            }))
            .filter(x => x.quantity > 0);
    }, [data, isFree]);

    const freeQuantity = useMemo(() => {
        if (!isFree) return undefined;
        const qty = Number(data?.cart?.quantity || 0);
        return qty > 0 ? qty : undefined;
    }, [data, isFree]);

    const participants = useMemo(() => {
        const a = data?.attendees || [];
        return a.map(p => ({
            ...(p.eventTicketTypeId ? { eventTicketTypeId: p.eventTicketTypeId } : {}),
            name: p.firstName ?? p.name ?? "",
            surName: p.lastName ?? p.surName ?? "",
            email: p.email ?? "",
            phone: p.phone ?? "",
        }));
    }, [data]);

    useEffect(() => {
        (async () => {
            try {
                setError(null);
                if (!eventId) {
                    throw new Error(t('order.confirmation.empty.eventId'));
                }
                if (!buyer?.name || !buyer?.surName || !buyer?.email) {
                    throw new Error(t('order.confirmation.empty.buyer'));
                }
                if (!items && !freeQuantity) {
                    throw new Error(t('order.confirmation.empty.positions'));
                }

                const idempotencyKey =
                    (crypto)?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;

                const payload = {
                    eventId,
                    buyer,
                    items,                // undefined dla free
                    freeQuantity,         // undefined dla paid
                    participants,
                };

                const order = await OrdersService.create(payload, idempotencyKey);
                const { orderId, number, status, totalGross, redirectUrl, tickets } = order;

                setOrder(order);
                setOrderId(orderId);
                Cookie.removeAll(eventId);
                if (redirectUrl) {
                    toast({
                        title: t("order.confirmation.payment.redirect") || "Przekierowanie do płatności…",
                        status: "info",
                        duration: 2500,
                        isClosable: true,
                    });
                    if (!redirectedRef.current) {
                        redirectedRef.current = true;
                        window.location.assign(redirectUrl);
                    }
                } else {
                    // free / od razu sfinalizowane
                    toast({
                        title: t("order.confirmation.payment.success") || "Zamówienie zapisane",
                        status: "success",
                        duration: 2500,
                        isClosable: true,
                    });
                }
            } catch (e) {
                setError(e);
                console.error(e);
                toast({
                    title: t("order.confirmation.payment.fail") || "Nie udało się sfinalizować zamówienia",
                    description: e?.message ?? String(e),
                    status: "error",
                    duration: 7000,
                    isClosable: true,
                });
                // wróć do wyboru biletów
                // navigate("/");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dwn = (ticketId, token) => {
        OrdersService.downloadTicket(ticketId, token);
    };
    const ticketTypes = data?.ticketTypes ?? [];
    const attendees = data?.attendees ?? [];

    return (
        <VStack spacing={6} textAlign="center" w={"full"} gap="48px">
            <Flex flexDirection={"column"} size="lg" alignItems={"center"} gap="32px">
                <svg width="96" height="95" viewBox="0 0 96 95" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M33.5 51.125L44.375 62L62.5 36.625M91.5 47.5C91.5 53.6287 88.455 59.0517 83.8005 62.3287C84.302 65.1937 84.1057 68.1374 83.2281 70.9105C82.3506 73.6836 80.8177 76.2043 78.7593 78.2593C76.7043 80.3177 74.1836 81.8506 71.4105 82.7281C68.6374 83.6056 65.6937 83.802 62.8287 83.3005C61.1574 85.6808 58.9371 87.6233 56.3559 88.9636C53.7746 90.3038 50.9084 91.0024 48 91C41.8713 91 36.4483 87.955 33.1713 83.3005C30.3063 83.8016 27.3627 83.605 24.5897 82.7275C21.8167 81.85 19.2959 80.3174 17.2407 78.2593C15.1823 76.2043 13.6494 73.6836 12.7719 70.9105C11.8944 68.1374 11.698 65.1937 12.1995 62.3287C9.8192 60.6574 7.87667 58.4371 6.53641 55.8559C5.19616 53.2746 4.49764 50.4084 4.50001 47.5C4.50001 41.3713 7.54501 35.9483 12.1995 32.6713C11.698 29.8063 11.8944 26.8626 12.7719 24.0895C13.6494 21.3164 15.1823 18.7957 17.2407 16.7407C19.2959 14.6826 21.8167 13.15 24.5897 12.2725C27.3627 11.395 30.3063 11.1984 33.1713 11.6995C34.8429 9.31949 37.0632 7.37718 39.6444 6.03696C42.2256 4.69674 45.0916 3.99804 48 4C54.1287 4 59.5517 7.045 62.8287 11.6995C65.6937 11.1984 68.6373 11.395 71.4103 12.2725C74.1833 13.15 76.7041 14.6826 78.7593 16.7407C80.8174 18.7959 82.35 21.3167 83.2275 24.0897C84.105 26.8627 84.3016 29.8063 83.8005 32.6713C86.1808 34.3426 88.1233 36.5629 89.4636 39.1441C90.8039 41.7254 91.5024 44.5916 91.5 47.5Z"
                        stroke={error ? "orange" : "#7A1F35"} strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {order &&
                    <Text fontFamily="body" fontSize="28px" fontWeight="800" color={'#7A1F35'}>
                        {t('order.confirmation.success')}
                    </Text>
                }
                {error &&
                    <Text fontFamily="body" fontSize="28px" fontWeight="800" color={'orange'}>
                        {error?.response?.data}
                    </Text>
                }
            </Flex>
            {orderId ? (
                <Flex flexDirection={"column"}>
                    <Box fontFamily="body" fontSize="20px" fontWeight="500" gap="24px" alignItems={"center"}
                        textAlign={"center"}>
                        <Box mb="24px" color={'#4B3F42'}>
                            <Text>
                                {t('order.confirmation.payment.title')}
                            </Text>
                            <Text>
                                {t('order.confirmation.payment.message')}
                            </Text>
                        </Box>
                        <Text color={'#A8A8A8'}>{t('order.form.number')} {order?.number}</Text>
                    </Box>
                </Flex>
            ) : (
                !error &&
                <Text>{t('order.processing')}</Text>
            )}
            {order &&
                <Box textAlign={"left"} w={"full"} mt="48px">
                    <Heading size="md" mb="16px">
                        <Flex justifyContent={"space-between"}>
                            <Text>{t('order.form.your_tickets')}</Text>
                        </Flex>
                    </Heading>
                    <SummaryTickets attendees={attendees} ticketTypes={ticketTypes} dwn={dwn} order={order} t={t} />
                </Box>
            }

            <Flex justify="space-between" gap="16px">
                <Button
                    bgColor={'#FFFFFF'}
                    color={'#7A1F35'}
                    fontFamily="body"
                    fontWeight="400"
                    fontSize="18px"
                    alignSelf="flex-end"
                    py="30px"
                    px="50px"
                    gap="12px"
                    style={{ border: '1.5px solid #7A1F35' }}
                    onClick={() => navigate("/")}>
                    <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10.5 20L3 12.5M3 12.5L10.5 5M3 12.5L21 12.5" stroke="#7A1F35" strokeWidth="1.5"
                            strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {t('order.form.to_main')}
                </Button>
            </Flex>
        </VStack>
    );
}

export default ConfirmationStep;
